/**
 * Vendor picker: search the shared list, or create a new vendor deliberately.
 *
 * The old field was free text, which is why "Stone Collective", "stone
 * collective " and "STONE COLLECTIVE" became three vendors and no one could ask
 * what any of them had quoted before.
 *
 * Creating is an explicit action rather than a side effect of typing. That
 * distinction is the whole design: a field that silently creates on blur is a
 * free-text field with extra steps, and it produces exactly the duplicates the
 * vendors table exists to prevent.
 */

import * as React from "react";
import {
	createVendor,
	findVendors,
	type VendorIdentity,
} from "@/features/projects/services/quotes-api.service";

interface VendorTypeaheadProps {
	value: VendorIdentity | null;
	onChange: (vendor: VendorIdentity | null) => void;
	placeholder?: string;
}

export function VendorTypeahead({
	value,
	onChange,
	placeholder = "Search vendors",
}: VendorTypeaheadProps) {
	const [query, setQuery] = React.useState("");
	const [results, setResults] = React.useState<VendorIdentity[]>([]);
	const [open, setOpen] = React.useState(false);
	const [creating, setCreating] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	// Debounced, because a typeahead firing per keystroke against a shared table
	// is also the easiest way to enumerate it.
	React.useEffect(() => {
		if (query.trim().length < 2) {
			setResults([]);
			return;
		}
		let cancelled = false;
		const timer = setTimeout(async () => {
			try {
				const found = await findVendors(query);
				if (!cancelled) setResults(found);
			} catch {
				if (!cancelled) setResults([]);
			}
		}, 250);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query]);

	const handleCreate = async () => {
		const name = query.trim();
		if (!name) return;
		setCreating(true);
		setError(null);
		try {
			const { vendor } = await createVendor({ name });
			onChange(vendor);
			setOpen(false);
			setQuery("");
		} catch (err) {
			// A duplicate is the useful failure here: the server names the existing
			// vendor so the designer can pick it instead of creating a second one.
			setError(
				(err as { response?: { data?: { message?: string } } })?.response?.data
					?.message ?? "Could not create that vendor.",
			);
		} finally {
			setCreating(false);
		}
	};

	if (value) {
		return (
			<div
				style={{
					flex: 1,
					display: "flex",
					alignItems: "center",
					gap: 6,
					padding: "8px 10px",
					border: "1px solid var(--color-border-default)",
					borderRadius: 6,
					background: "var(--color-dashboard-paper)",
					fontSize: 13,
					color: "var(--color-dashboard-text)",
				}}
			>
				<span style={{ flex: 1 }}>
					{value.name}
					{value.city ? (
						<span style={{ color: "var(--color-dashboard-muted)" }}>
							{" "}
							· {value.city}
						</span>
					) : null}
					{value.isVerified ? (
						<span style={{ color: "var(--color-dashboard-success)" }}>
							{" "}
							· verified
						</span>
					) : null}
				</span>
				<button
					type="button"
					onClick={() => onChange(null)}
					style={{
						border: 0,
						background: "transparent",
						cursor: "pointer",
						color: "var(--color-dashboard-muted)",
						fontSize: 12,
					}}
				>
					change
				</button>
			</div>
		);
	}

	const exactShown = results.some(
		(r) => r.name.toLowerCase() === query.trim().toLowerCase(),
	);

	return (
		<div style={{ flex: 1, position: "relative" }}>
			<input
				type="text"
				value={query}
				onChange={(e) => {
					setQuery(e.target.value);
					setOpen(true);
					setError(null);
				}}
				onFocus={() => setOpen(true)}
				placeholder={placeholder}
				style={{
					width: "100%",
					padding: "8px 10px",
					border: "1px solid var(--color-border-default)",
					borderRadius: 6,
					background: "var(--color-dashboard-paper)",
					fontSize: 13,
					color: "var(--color-dashboard-text)",
					outline: "none",
				}}
			/>
			{open && query.trim().length >= 2 && (
				<div
					style={{
						position: "absolute",
						top: "100%",
						left: 0,
						right: 0,
						zIndex: 20,
						marginTop: 4,
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-border-default)",
						borderRadius: 6,
						boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
						maxHeight: 220,
						overflowY: "auto",
					}}
				>
					{results.map((r) => (
						<button
							key={r.id}
							type="button"
							onClick={() => {
								onChange(r);
								setOpen(false);
								setQuery("");
							}}
							style={{
								display: "block",
								width: "100%",
								textAlign: "left",
								padding: "8px 10px",
								border: 0,
								background: "transparent",
								cursor: "pointer",
								fontSize: 13,
								color: "var(--color-dashboard-text)",
							}}
						>
							{r.name}
							{/* City is shown because the same trading name in two cities is
							    genuinely two vendors, and the dropdown is where that has to
							    be distinguishable. */}
							{r.city ? (
								<span style={{ color: "var(--color-dashboard-muted)" }}>
									{" "}
									· {r.city}
								</span>
							) : null}
							{r.isVerified ? (
								<span style={{ color: "var(--color-dashboard-success)" }}>
									{" "}
									· verified
								</span>
							) : null}
						</button>
					))}

					{!exactShown && (
						<button
							type="button"
							onClick={handleCreate}
							disabled={creating}
							style={{
								display: "block",
								width: "100%",
								textAlign: "left",
								padding: "8px 10px",
								border: 0,
								borderTop: results.length
									? "1px solid var(--color-dashboard-border-soft)"
									: 0,
								background: "transparent",
								cursor: "pointer",
								fontSize: 13,
								color: "var(--color-dashboard-accent-dark)",
							}}
						>
							{creating ? "Creating…" : `Create "${query.trim()}"`}
							{results.length > 0 && (
								<span
									style={{
										color: "var(--color-dashboard-muted)",
										fontSize: 11,
									}}
								>
									{" "}
									— check the matches above first
								</span>
							)}
						</button>
					)}

					{error && (
						<div
							style={{
								padding: "8px 10px",
								fontSize: 12,
								color: "var(--color-dashboard-pill-awaiting)",
								borderTop: "1px solid var(--color-dashboard-border-soft)",
							}}
						>
							{error}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
